import {TeejitRestClient} from './teejit-rest-client.js';

const pending = new WeakSet();

// Move existing rows to preserve playing media, answers and the original learning order.
const regroupCertificateRequirements = (item, repeatRequired) => {
    const row = item.closest?.('[data-certificate-requirement-row]');
    const list = row?.closest('[data-certificate-requirements]');
    if (!list) return;
    const nextState = repeatRequired ? '1' : '0';
    if (row.dataset.repeatRequired === nextState) return;
    row.dataset.repeatRequired = nextState;
    const rows = Array.from(list.querySelectorAll('[data-certificate-requirement-row]'))
        .sort((a, b) => a.dataset.requirementOrder.localeCompare(b.dataset.requirementOrder, undefined, {numeric: true}));
    const learning = rows.filter((entry) => entry.dataset.learningRequirement === '1');
    const repeat = learning.filter((entry) => entry.dataset.repeatRequired === '1');
    const previousSections = Array.from(list.querySelectorAll(':scope > [data-certificate-requirements-section]'));
    // Older browsers cannot move iframe players without reloading them. Update badges now;
    // the next page render will regroup those rows without interrupting playback.
    if (typeof list.moveBefore !== 'function' && rows.some((entry) => entry.querySelector('iframe'))) return;
    const moveRow = (container, entry) => {
        if (typeof container.moveBefore === 'function') container.moveBefore(entry, null);
        else container.append(entry);
    };
    if (!repeat.length || repeat.length === learning.length) {
        rows.forEach((entry) => moveRow(list, entry));
    } else {
        const section = (title) => {
            const container = document.createElement('section');
            container.className = 'kh-certificate-requirements-section';
            container.setAttribute('data-certificate-requirements-section', '');
            const heading = document.createElement('h3');
            heading.textContent = title;
            container.append(heading);
            list.append(container);
            return container;
        };
        const repeatSection = section(list.dataset.repeatTitle);
        const otherSection = section(list.dataset.otherTitle);
        rows.forEach((entry) => moveRow(repeat.includes(entry) ? repeatSection : otherSection, entry));
    }
    previousSections.forEach((section) => section.remove());
};

// Certificate badges come from the server; never show a provisional check first.
export const setContentCompletionState = (item, completed) => {
    const summary = item?.querySelector('.accordion-header .summary-top');
    if (!summary || item.querySelector('.kh-content-certificate-notices')) {
        return;
    }
    const current = summary.querySelector('.summary-success');
    if (!completed) {
        current?.remove();
        return;
    }
    if (current) {
        return;
    }
    const badge = document.createElement('div');
    badge.className = 'kh-content-certificate-status summary-success';
    const label = window.khThemeRuntime?.contentCertificate?.completed || 'Content completed';
    badge.setAttribute('role', 'img');
    badge.setAttribute('aria-label', label);
    badge.title = label;
    const icon = document.createElement('span');
    icon.className = 'kh-content-seen-icon material-symbols-outlined';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'check';
    badge.append(icon);
    summary.prepend(badge);
};

// Refresh only certificate UI; keep playing media and test answers in place.
export const refreshContentCertificateContext = async (item) => {
    if (!item?.querySelector('.kh-content-certificate-notices') || pending.has(item)) {
        return;
    }
    const {contentId, contentTypeId, contentableType} = item.dataset;
    if (!contentId || !contentTypeId || !contentableType) {
        return;
    }
    pending.add(item);
    try {
        const currentNotices = item.querySelector('.kh-content-certificate-notices');
        const {certificateScopeId, requirementIndex, learningUnitIndex} = currentNotices?.dataset || {};
        const certificateFragment = Number(certificateScopeId) > 0 && requirementIndex !== undefined;
        const path = certificateFragment
            ? 'theme/certificates/' + encodeURIComponent(certificateScopeId) + '/requirements/' + encodeURIComponent(requirementIndex)
                + '/html' + (learningUnitIndex !== undefined ? '?learning_unit_index=' + encodeURIComponent(learningUnitIndex) : '')
            : 'content/' + encodeURIComponent(contentId) + '/items/' + encodeURIComponent(contentTypeId)
                + '/html?contentable_type=' + encodeURIComponent(contentableType);
        const html = await new TeejitRestClient().requestHtml(
            path,
            {noCache: true},
        );
        if (!item.isConnected) {
            return;
        }
        const template = document.createElement('template');
        template.innerHTML = html;
        const notices = template.content.querySelector('.kh-content-certificate-notices');
        if (!notices) {
            return;
        }
        if (currentNotices?.dataset?.showCertificateLink === '0') {
            notices.dataset.showCertificateLink = '0';
            notices.querySelectorAll('.kh-content-certificate-link').forEach((link) => link.remove());
        }
        currentNotices?.replaceWith(notices);
        const summary = item.querySelector('.accordion-header .summary-top');
        const next = template.content.querySelector('.kh-content-certificate-status');
        if (summary) {
            summary.querySelectorAll(':scope > .kh-content-certificate-status, :scope > .summary-success, :scope > .summary-repeat-required, :scope > .kh-content-repeat-ribbon')
                .forEach((badge) => badge.remove());
            if (next) {
                summary.prepend(next);
            }
            regroupCertificateRequirements(item, !!next?.classList.contains('summary-repeat-required'));
        }
    } catch (error) {
        // Keep the last server-confirmed notice when a refresh is unavailable.
    } finally {
        pending.delete(item);
    }
};
